import { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Alert, CircularProgress,
  Divider, Typography, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { reportProduction, getProductionReports, type ProductionOrderListItem } from '../../api/productieOrdine'
import { errorMessage } from '../../api/client'

export default function ReportProductionDialog({ target, onClose }: { target: ProductionOrderListItem | null; onClose: () => void }) {
  const open = target !== null
  const qc = useQueryClient()
  const [goodQuantity, setGoodQuantity] = useState('')
  const [scrapQuantity, setScrapQuantity] = useState('')
  const [finishedLotNumber, setFinishedLotNumber] = useState('')
  const [notes, setNotes] = useState('')

  const { data: reportsData } = useQuery({
    queryKey: ['productie', 'ordine', target?.id, 'rapoarte'], queryFn: () => getProductionReports(target!.id), enabled: open,
  })

  const mutation = useMutation({
    mutationFn: () => reportProduction(target!.id, {
      goodQuantityThousands: Number(goodQuantity) || 0, scrapQuantityThousands: Number(scrapQuantity) || 0,
      finishedLotNumber: finishedLotNumber || null, zoneId: null, locationId: null, notes: notes || null,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['productie', 'ordine'] })
      setGoodQuantity(''); setScrapQuantity(''); setFinishedLotNumber(''); setNotes('')
    },
  })

  const handleClose = () => { mutation.reset(); onClose() }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Raportează producție — {target?.number}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={2}>
            <TextField type="number" label="Cantitate bună (mii buc)" value={goodQuantity} onChange={(e) => setGoodQuantity(e.target.value)} sx={{ flexGrow: 1 }} />
            <TextField type="number" label="Rebut (mii buc)" value={scrapQuantity} onChange={(e) => setScrapQuantity(e.target.value)} sx={{ flexGrow: 1 }} />
          </Stack>
          <TextField label="Lot produs finit (opțional — generat automat dacă lipsește)" value={finishedLotNumber} onChange={(e) => setFinishedLotNumber(e.target.value)} fullWidth />
          <TextField label="Note" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} />
          {mutation.isSuccess && (
            mutation.data?.scrapRateWarning
              ? <Alert severity="warning">Raportat — dar rata de rebut cumulată depășește 5%, verificați utilajul/matrița (FR-OPE-007).</Alert>
              : <Alert severity="success">Raportat.</Alert>
          )}
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}

          {reportsData && reportsData.reports.length > 0 && (
            <>
              <Divider />
              <Typography variant="subtitle2">Rapoarte anterioare</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell>Data</TableCell><TableCell>Lot</TableCell><TableCell align="right">Bun</TableCell><TableCell align="right">Rebut</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {reportsData.reports.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{new Date(r.reportedAt).toLocaleString('ro-RO')}</TableCell>
                      <TableCell><code>{r.finishedLotNumber}</code></TableCell>
                      <TableCell align="right">{r.goodQuantityThousands}</TableCell>
                      <TableCell align="right">{r.scrapQuantityThousands}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="subtitle2">Consum real vs. normat (FR-OPE-006)</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell>Componentă</TableCell><TableCell align="right">Normat</TableCell><TableCell align="right">Real</TableCell><TableCell align="right">Abatere</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {reportsData.consumptionDeviations.map((d) => (
                    <TableRow key={d.componentArticleId}>
                      <TableCell>{d.componentArticleCode} — {d.componentArticleDescription}</TableCell>
                      <TableCell align="right">{d.normedQuantity.toFixed(3).replace('.', ',')}</TableCell>
                      <TableCell align="right">{d.actualQuantity.toFixed(3).replace('.', ',')}</TableCell>
                      <TableCell align="right">{d.deviationPercent.toFixed(1).replace('.', ',')}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose}>Închide</Button>
        <Button variant="contained" disabled={mutation.isPending} onClick={() => mutation.mutate()}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Raportează
        </Button>
      </DialogActions>
    </Dialog>
  )
}
